import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';
import useSubjectImportedDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectImportedDocuments';
import describeDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Helpers/describeDocument';
import describeInterview from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/describeInterview';
import ElementDescription from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/ElementDescription';
import type WorkflowRunSourceView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSourceView';
import * as React from 'react';

/**
 * Titles are never carried by an element: they are resolved here from the interviews and documents of the
 * dossier. An element whose target is not loaded yet reads « Entretien n°… » /
 * « Document n°… » / « Document importé n°… » rather than an empty cell.
 */
const useElementDescriber = (
	subject: SubjectAddress,
): {
	readonly describeElement: (element: WorkflowRunSourceView) => ElementDescription;
	readonly labelOfDocumentType: (documentTypeId: number) => string;
} => {
	const { data: interviews } = useSubjectInterviews(subject);
	const { data: documents } = useSubjectDocuments(subject);
	const { data: importedDocuments } = useSubjectImportedDocuments(subject);
	const { documentTypes } = useAccountDocumentTypes();

	const labelOfDocumentType = React.useCallback(
		(documentTypeId: number): string => documentTypes?.labelOf(documentTypeId) ?? 'Document',
		[documentTypes],
	);

	const describeElement = React.useCallback(
		(element: WorkflowRunSourceView): ElementDescription => {
			if (element.interviewId !== null) {
				const interview = interviews?.findById(element.interviewId) ?? null;

				return interview !== null
					? describeInterview(interview)
					: new ElementDescription(`Entretien n°${element.interviewId}`, element.category.label);
			}

			if (element.documentId !== null) {
				const document = documents?.findById(element.documentId) ?? null;

				return document !== null
					? describeDocument(document, labelOfDocumentType(document.documentTypeId))
					: new ElementDescription(`Document n°${element.documentId}`, element.category.label);
			}

			if (element.importedDocumentId !== null) {
				const importedDocument = importedDocuments?.findById(element.importedDocumentId) ?? null;

				return importedDocument !== null
					? new ElementDescription(
							importedDocument.name,
							`Document importé · ${importedDocument.format.label}`,
						)
					: new ElementDescription(
							`Document importé n°${element.importedDocumentId}`,
							element.category.label,
						);
			}

			return new ElementDescription('Élément', element.category.label);
		},
		[interviews, documents, importedDocuments, labelOfDocumentType],
	);

	return { describeElement, labelOfDocumentType };
};

export default useElementDescriber;
